/**
 * AUXcanvas TrailerPlannerPage
 *
 * Smart orchestrator for the Trailer Planner. Owns all state and drag logic.
 *
 * Drag implementation: Pointer Events + setPointerCapture (NOT HTML5 drag).
 * - onPointerDown on a vehicle → captures pointer, sets drag state
 * - window pointermove → update ghost card position + find hovered slot
 * - window pointerup → drop into slot or cancel
 * - Ghost card: position:fixed, pointer-events:none so elementFromPoint sees slots
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import api, { type EquipmentProfile, type EquipmentSpec, type EquipmentCombo, type AutoHaulerGeometry } from '../../lib/api'
import type { VehicleInfo, SlotId, DragState } from './types'
import { TrailerCanvas } from './TrailerCanvas'
import { VehicleQueue } from './VehicleQueue'
import { TrailerToolbar } from './TrailerToolbar'
import { useAuth } from '../../lib/auth-context'
import { TrailerWarnings } from './TrailerWarnings'
import { SLOT_META, vehicleLabel, vehicleColorHex } from './types'

type LoadingState = 'idle' | 'loading' | 'error'

const DEFAULT_EQUIPMENT_PROFILE: EquipmentProfile = {
  tractorSteerAxles: 1,
  tractorDriveAxles: 2,
  tractorSteerAxleLimit: 12000,
  tractorDriveAxleLimit: 34000,
  tractorWheelbase: null,
  tractorGcwr: 80000,
  tractorEmptyWeight: null,
  trailerAxles: 2,
  trailerAxleLimit: 34000,
  trailerDeckLength: 240,
  trailerDeckWidth: 102,
  trailerDeckHeight: 80,
  trailerRampLength: null,
  trailerEmptyWeight: null,
  isDoubleDeck: true,
  overrideGcwrLimit: null,
  overrideAxleLimits: false,
}

function toNullableNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

export function TrailerPlannerPage() {
  const { jobId } = useParams<{ jobId: string }>()

  // ─── Server-fetched data ───────────────────────────────────────────────────
  const [allVehicles, setAllVehicles] = useState<VehicleInfo[]>([])
  const [jobNumber, setJobNumber] = useState<string | undefined>()
  const [warnings, setWarnings] = useState<string[]>([])
  const [driverEditPermission, setDriverEditPermission] = useState<any>({ unlocked: false })
  const [canonicalTrailerPlan, setCanonicalTrailerPlan] = useState<any>({ deckStates: {}, rampStates: {} })
  const { hasRole } = useAuth()
  const isDriver = hasRole('DRIVER')
  const [trailerSpecs, setTrailerSpecs] = useState<EquipmentSpec[]>([])
  const [truckSpecs, setTruckSpecs] = useState<EquipmentSpec[]>([])
  const [combos, setCombos] = useState<EquipmentCombo[]>([])
  const [selectedTrailerSpecId, setSelectedTrailerSpecId] = useState('')
  const [selectedTruckSpecId, setSelectedTruckSpecId] = useState('')
  const [selectedTrailerSpec, setSelectedTrailerSpec] = useState<EquipmentSpec | null>(null)
  const [selectedTruckSpec, setSelectedTruckSpec] = useState<EquipmentSpec | null>(null)
  const [selectedComboKey, setSelectedComboKey] = useState('')
  const [selectedCombo, setSelectedCombo] = useState<EquipmentCombo | null>(null)
  const [autoHaulerGeometry, setAutoHaulerGeometry] = useState<AutoHaulerGeometry | null>(null)
  const [isApplyingCombo, setIsApplyingCombo] = useState(false)
  const [isResolvingProfiles, setIsResolvingProfiles] = useState(false)
  const [fitSummary, setFitSummary] = useState<Record<string, { status: string; warnings: string[]; riskFlags: Record<string, boolean>; dims: { lengthIn: number|null; heightIn: number|null; curbWeightLb: number|null; wheelbaseIn: number|null } }>>({})
  const [equipmentProfile, setEquipmentProfile] = useState<EquipmentProfile>(DEFAULT_EQUIPMENT_PROFILE)
  const [savedEquipmentProfile, setSavedEquipmentProfile] = useState<EquipmentProfile>(DEFAULT_EQUIPMENT_PROFILE)
  const [loadingState, setLoadingState] = useState<LoadingState>('loading')
  const [error, setError] = useState<string | null>(null)

  // ─── Layout state ─────────────────────────────────────────────────────────
  // assignments: Map<slotId, vehicleId>
  const [assignments, setAssignments] = useState<Map<SlotId, string>>(new Map())
  // Server-saved snapshot (to enable reset)
  const [savedAssignments, setSavedAssignments] = useState<Map<SlotId, string>>(new Map())
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null)
  const [selectedSlotId, setSelectedSlotId] = useState<SlotId | null>(null)
  const isDirty =
    JSON.stringify([...assignments].sort()) !== JSON.stringify([...savedAssignments].sort())

  // ─── Action states ─────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false)
  const [isArranging, setIsArranging] = useState(false)
  const [isSavingProfile, setIsSavingProfile] = useState(false)

  // ─── Drag state ────────────────────────────────────────────────────────────
  const [drag, setDrag] = useState<DragState | null>(null)
  const [hoveredSlot, setHoveredSlot] = useState<SlotId | null>(null)
  const dragRef = useRef<DragState | null>(null)
  // keep ref in sync with state for window listener closures
  dragRef.current = drag

  // ─── Load layout from server ───────────────────────────────────────────────
  const loadLayout = useCallback(async () => {
    if (!jobId) return
    setLoadingState('loading')
    setError(null)
    try {
      const r = await api.trailerLayout.get(jobId)
      const data = r.data as any

      // Collect all vehicles (assigned + unassigned) with full info
      const vehicleMap = new Map<string, VehicleInfo>()
      const addVehicle = (v: any) => {
        vehicleMap.set(v.vehicleId, {
          vehicleId: v.vehicleId,
          vin: v.vin,
          make: v.make,
          model: v.model,
          year: v.year,
          color: v.color,
          bodyStyle: v.bodyStyle,
        })
      }
      ;(data.assigned ?? []).forEach(addVehicle)
      ;(data.unassigned ?? []).forEach(addVehicle)
      setAllVehicles([...vehicleMap.values()])

      // Build assignment map
      const map = new Map<SlotId, string>()
      ;(data.assigned ?? []).forEach((a: any) => {
        if (a.slotId && a.vehicleId) map.set(a.slotId, a.vehicleId)
      })
      setAssignments(new Map(map))
      setSavedAssignments(new Map(map))

      const profile = (data.equipmentProfile ?? DEFAULT_EQUIPMENT_PROFILE) as EquipmentProfile
      setEquipmentProfile(profile)
      setSavedEquipmentProfile(profile)

      // Combo / auto-hauler geometry
      if (data.autoHaulerGeometry) setAutoHaulerGeometry(data.autoHaulerGeometry)
      if (data.comboMeta?.comboKey) setSelectedComboKey(data.comboMeta.comboKey)

      if (data.jobNumber) setJobNumber(data.jobNumber)
      setWarnings(data.warnings ?? [])
      setDriverEditPermission(data.driverEditPermission ?? { unlocked: false })
      setCanonicalTrailerPlan(data.trailerPlan ?? { deckStates: {}, rampStates: {} })
      setSelectedVehicleId(null)
      setSelectedSlotId(null)
      setLoadingState('idle')
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err?.message ?? 'Failed to load trailer layout'
      setError(msg)
      setLoadingState('error')
    }
  }, [jobId])

  useEffect(() => { loadLayout() }, [loadLayout])

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const [trailersRes, trucksRes, combosRes] = await Promise.all([
          api.equipment.list('trailer', { limit: 100 }),
          api.equipment.list('truck', { limit: 100 }),
          api.equipment.combos({ limit: 100 }),
        ])
        if (!active) return
        setTrailerSpecs(trailersRes.data.specs ?? [])
        setTruckSpecs(trucksRes.data.specs ?? [])
        setCombos(combosRes.data.combos ?? [])
      } catch {
        if (!active) return
        setTrailerSpecs([])
        setTruckSpecs([])
        setCombos([])
      }
    })()

    return () => { active = false }
  }, [])

  // ─── Derived: assignment map with full VehicleInfo ─────────────────────────
  const vehicleById = new Map(allVehicles.map(v => [v.vehicleId, v]))

  const assignedVehicleMap = new Map<SlotId, VehicleInfo>()
  assignments.forEach((vehicleId, slotId) => {
    const v = vehicleById.get(vehicleId)
    if (v) assignedVehicleMap.set(slotId, v)
  })

  const assignedVehicleIds = new Set(assignments.values())
  const unassignedVehicles = allVehicles.filter(v => !assignedVehicleIds.has(v.vehicleId))
  const selectedVehicle = selectedVehicleId ? vehicleById.get(selectedVehicleId) ?? null : null
  const selectedSlotLabel = selectedSlotId ? SLOT_META[selectedSlotId]?.label ?? selectedSlotId : null
  const isDriverEditLocked = isDriver && !driverEditPermission?.unlocked

  const clearTapSelection = useCallback(() => {
    setSelectedVehicleId(null)
    setSelectedSlotId(null)
  }, [])

  const selectVehicle = useCallback((vehicleId: string) => {
    if (isDriverEditLocked) return
    setSelectedVehicleId(prev => (prev === vehicleId ? null : vehicleId))
  }, [isDriverEditLocked])

  const selectSlot = useCallback((slotId: SlotId) => {
    if (isDriverEditLocked) return
    setSelectedSlotId(prev => (prev === slotId ? null : slotId))
  }, [isDriverEditLocked])

  const moveSelectedVehicleToSlot = useCallback(() => {
    if (!selectedVehicleId || !selectedSlotId || isDriverEditLocked) return
    setAssignments(prev => {
      const next = new Map(prev)
      let sourceSlotId: SlotId | null = null
      for (const [slotId, vehicleId] of next.entries()) {
        if (vehicleId === selectedVehicleId) {
          sourceSlotId = slotId
          break
        }
      }
      if (sourceSlotId) next.delete(sourceSlotId)
      next.delete(selectedSlotId)
      next.set(selectedSlotId, selectedVehicleId)
      return next
    })
    clearTapSelection()
  }, [clearTapSelection, isDriverEditLocked, selectedSlotId, selectedVehicleId])

  // ─── Window-level pointer listeners for drag ───────────────────────────────
  useEffect(() => {
    function onPointerMove(e: PointerEvent) {
      const d = dragRef.current
      if (!d) return
      setDrag(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)

      // Find slot under pointer (ghost card has pointer-events:none so this works)
      const el = document.elementFromPoint(e.clientX, e.clientY)
      const slotEl = el?.closest('[data-slot-id]')
      const sid = slotEl?.getAttribute('data-slot-id') ?? null
      setHoveredSlot(sid as SlotId | null)
    }

    function onPointerUp(e: PointerEvent) {
      const d = dragRef.current
      if (!d) return

      const el = document.elementFromPoint(e.clientX, e.clientY)
      const slotEl = el?.closest('[data-slot-id]')
      const targetSlot = slotEl?.getAttribute('data-slot-id') as SlotId | null

      if (targetSlot) {
        setAssignments(prev => {
          const next = new Map(prev)
          // Remove vehicle from its source slot
          if (d.sourceSlotId) next.delete(d.sourceSlotId)
          // Evict any vehicle currently in target slot → move back to queue
          // (just delete it — vehicle goes to unassigned automatically)
          next.delete(targetSlot)
          next.set(targetSlot, d.vehicleId)
          return next
        })
      }

      setDrag(null)
      dragRef.current = null
      setHoveredSlot(null)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, []) // intentionally empty — drag ref keeps it fresh

  // ─── Drag handlers ─────────────────────────────────────────────────────────
  function startDrag(e: React.PointerEvent, vehicleId: string, sourceSlotId: SlotId | null) {
    e.preventDefault()
    if (isDriver && e.pointerType !== 'mouse' && e.pointerType !== 'pen') return
    e.currentTarget.setPointerCapture(e.pointerId)
    if (isDriver && !driverEditPermission?.unlocked) return
    const state: DragState = { vehicleId, sourceSlotId, x: e.clientX, y: e.clientY }
    setDrag(state)
    dragRef.current = state
  }

  const handleQueueDragStart = useCallback((e: React.PointerEvent, vehicleId: string) => {
    startDrag(e, vehicleId, null)
  }, [])

  const handleSlotDragStart = useCallback((e: React.PointerEvent, vehicleId: string, sourceSlotId: SlotId) => {
    startDrag(e, vehicleId, sourceSlotId)
  }, [])

  // ─── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!jobId) return
    setIsSaving(true)
    const payload = [...assignments.entries()].map(([slotId, vehicleId]) => ({ slotId, vehicleId }))
    try {
      const r = await api.trailerLayout.save(jobId, payload)
      const data = r.data as any
      // Reflect saved state
      const map = new Map<SlotId, string>()
      ;(data.assignments ?? payload).forEach((a: any) => map.set(a.slotId, a.vehicleId))
      setSavedAssignments(new Map(map))
      if (data.warnings) setWarnings(data.warnings)
      setSavedAt(new Date().toISOString())
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Save failed. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  // ─── Auto Arrange ──────────────────────────────────────────────────────────
  const handleAutoArrange = async () => {
    if (!jobId) return
    setIsArranging(true)
    try {
      const r = await api.trailerLayout.autoArrange(jobId)
      const data = r.data as any
      const map = new Map<SlotId, string>()
      ;(data.assignments ?? []).forEach((a: any) => map.set(a.slotId, a.vehicleId))
      setAssignments(map)
      setSavedAssignments(new Map(map))
      // Combine general warnings + fit-specific warnings
      const allWarns = [
        ...(data.warnings ?? []),
        ...(data.fitWarnings ?? []),
        ...(data.blockedVehicles?.map((b: any) => `BLOCKED ${b.vin ?? b.vehicleId}: ${b.reasons?.join('; ')}`) ?? []),
        ...(data.missingVehicleProfiles?.length > 0
          ? [`${data.missingVehicleProfiles.length} vehicle(s) missing fit profiles — run Resolve Vehicle Fit Data`]
          : []),
      ]
      setWarnings([...new Set(allWarns)])
      if (data.equipmentProfile) {
        setEquipmentProfile(data.equipmentProfile)
        setSavedEquipmentProfile(data.equipmentProfile)
      }
      if (data.autoHaulerGeometry) setAutoHaulerGeometry(data.autoHaulerGeometry)
      setSavedAt(new Date().toISOString())
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Auto-arrange failed. Please try again.')
    } finally {
      setIsArranging(false)
    }
  }

  // ─── Reset to saved ────────────────────────────────────────────────────────
  const handleReset = () => {
    setAssignments(new Map(savedAssignments))
    setEquipmentProfile(savedEquipmentProfile)
    setWarnings([])
  }

  const isProfileDirty = JSON.stringify(equipmentProfile) !== JSON.stringify(savedEquipmentProfile)

  const updateProfileField = <K extends keyof EquipmentProfile>(key: K, value: EquipmentProfile[K]) => {
    setEquipmentProfile((prev) => ({ ...prev, [key]: value }))
  }

  const handleProfileNumberChange = <K extends keyof EquipmentProfile>(key: K, rawValue: string) => {
    const nextValue = toNullableNumber(rawValue)
    updateProfileField(key, nextValue as EquipmentProfile[K])
  }

  const handleProfileIntegerChange = <K extends keyof EquipmentProfile>(key: K, rawValue: string) => {
    const n = toNullableNumber(rawValue)
    updateProfileField(key, (n == null ? null : Math.max(Math.trunc(n), 0)) as EquipmentProfile[K])
  }

  const handleProfileSave = async () => {
    if (!jobId) return
    setIsSavingProfile(true)
    try {
      const response = await api.trailerLayout.updateProfile(jobId, equipmentProfile)
      const nextProfile = response.data.equipmentProfile ?? equipmentProfile
      setEquipmentProfile(nextProfile)
      setSavedEquipmentProfile(nextProfile)
      if (response.data.warnings) setWarnings(response.data.warnings)
      await loadLayout()
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Failed to save equipment profile')
    } finally {
      setIsSavingProfile(false)
    }
  }

  const handleApplySpec = async (specId: string, category: 'truck' | 'trailer') => {
    if (!jobId || !specId) return
    try {
      const response = await api.trailerLayout.applySpec(jobId, specId)
      const nextProfile = response.data.equipmentProfile ?? equipmentProfile
      setEquipmentProfile(nextProfile)
      setSavedEquipmentProfile(nextProfile)
      if (response.data.warnings) setWarnings(response.data.warnings)
      if (category === 'truck') {
        setSelectedTruckSpecId(specId)
        setSelectedTruckSpec(response.data.spec ?? truckSpecs.find((s) => s.id === specId) ?? null)
      }
      if (category === 'trailer') {
        setSelectedTrailerSpecId(specId)
        setSelectedTrailerSpec(response.data.spec ?? trailerSpecs.find((s) => s.id === specId) ?? null)
      }
      // Clear combo selection when manually changing individual spec
      setSelectedComboKey('')
      setSelectedCombo(null)
      await loadLayout()
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Failed to apply equipment spec')
    }
  }

  const handleApplyCombo = async (comboKey: string) => {
    if (!jobId || !comboKey) return
    setIsApplyingCombo(true)
    try {
      const response = await api.trailerLayout.applyCombo(jobId, comboKey)
      const nextProfile = response.data.equipmentProfile ?? equipmentProfile
      setEquipmentProfile(nextProfile)
      setSavedEquipmentProfile(nextProfile)
      if (response.data.warnings) setWarnings(response.data.warnings)
      if (response.data.autoHaulerGeometry) setAutoHaulerGeometry(response.data.autoHaulerGeometry)
      const combo = response.data.combo
      setSelectedComboKey(comboKey)
      setSelectedCombo(combo ?? combos.find(c => c.comboKey === comboKey) ?? null)
      // Clear individual spec selections — combo takes precedence
      setSelectedTruckSpecId('')
      setSelectedTruckSpec(null)
      setSelectedTrailerSpecId('')
      setSelectedTrailerSpec(null)
      await loadLayout()
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Failed to apply equipment combo')
    } finally {
      setIsApplyingCombo(false)
    }
  }

  const handleResolveProfiles = async () => {
    if (!jobId) return
    setIsResolvingProfiles(true)
    try {
      const resp = await api.vehicleFit.resolveJobProfiles(jobId)
      const summary: typeof fitSummary = {}
      for (const v of [...(resp.data.resolved ?? []), ...(resp.data.unresolved ?? [])]) {
        const riskFlags = (v as any).riskFlags ?? {}
        const dims = (v as any).dimensions ?? {}
        summary[v.vehicleId] = {
          status: v.profileStatus,
          warnings: v.warnings ?? [],
          riskFlags,
          dims: {
            lengthIn: dims.lengthIn ?? null,
            heightIn: dims.heightIn ?? null,
            curbWeightLb: dims.curbWeightLb ?? null,
            wheelbaseIn: dims.wheelbaseIn ?? null,
          },
        }
      }
      setFitSummary(summary)
      // Also reload layout to pick up updated job_vehicles.fit_profile_status
      await loadLayout()
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Failed to resolve vehicle fit profiles')
    } finally {
      setIsResolvingProfiles(false)
    }
  }

  // ─── Ghost card vehicle info ───────────────────────────────────────────────
  const dragVehicle = drag ? vehicleById.get(drag.vehicleId) : null

  // ─── Render ────────────────────────────────────────────────────────────────
  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl mb-2 animate-spin">⟳</div>
          <div className="text-gray-500 text-sm">Loading trailer layout…</div>
        </div>
      </div>
    )
  }

  if (loadingState === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-red-200 p-6 max-w-md w-full text-center space-y-3">
          <div className="text-2xl">⚠️</div>
          <div className="font-semibold text-red-700">Failed to load layout</div>
          <div className="text-sm text-red-600">{error}</div>
          <button onClick={loadLayout} className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/loads" className="hover:text-blue-600">Loads</Link>
          <span>›</span>
          <span className="text-gray-800 font-medium">Trailer Planner</span>
          {jobNumber && <span className="text-gray-500">— {jobNumber}</span>}
        </div>

        {/* Toolbar */}
        <TrailerToolbar
          jobId={jobId!}
          jobNumber={jobNumber}
          isSaving={isSaving}
          isArranging={isArranging}
          isDirty={isDirty}
          driverEditPermission={driverEditPermission}
          isDriver={isDriver}
          onToggleDriverPermission={async (unlock: boolean) => {
            try {
              const resp = await api.trailerLayout.setDriverPermission(jobId!, { driverEditUnlocked: unlock })
              setDriverEditPermission(resp.data.driverEditPermission ?? { unlocked: Boolean(unlock) })
            } catch (err: any) {
              alert(err?.response?.data?.error ?? 'Failed to set driver permission')
            }
          }}
          onSave={handleSave}
          onAutoArrange={handleAutoArrange}
          onReset={handleReset}
        />

        {isDriver && (
          <div className={`rounded-xl border p-4 space-y-3 ${isDriverEditLocked ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-gray-900">Tap Move Mode</div>
                <div className="text-sm text-gray-600">
                  {isDriverEditLocked
                    ? 'Trailer editing is locked. A dispatcher must unlock this load before you can move vehicles.'
                    : 'Tap a vehicle, tap a slot, then use Move Selected Vehicle to Slot.'}
                </div>
              </div>
              <div className={`text-xs font-medium px-2.5 py-1 rounded-full border ${isDriverEditLocked ? 'text-amber-800 bg-white border-amber-200' : 'text-blue-800 bg-white border-blue-200'}`}>
                {isDriverEditLocked ? 'View only' : 'Tap editing enabled'}
              </div>
            </div>

            <div className="flex flex-col gap-2 text-sm text-gray-700">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="font-medium">Selected vehicle:</span>
                <span className="text-gray-900">{selectedVehicle ? vehicleLabel(selectedVehicle) : 'None'}</span>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <span className="font-medium">Selected slot:</span>
                <span className="text-gray-900">{selectedSlotLabel ?? 'None'}</span>
              </div>
            </div>

            {isDriverEditLocked ? (
              <div className="text-sm text-amber-900 bg-white/70 border border-amber-200 rounded-lg px-3 py-2">
                Trailer editing is locked for drivers until a dispatcher unlocks this load.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={moveSelectedVehicleToSlot}
                  disabled={!selectedVehicleId || !selectedSlotId || isSaving || isArranging}
                  className="min-h-[44px] rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-blue-800"
                >
                  Move Selected Vehicle to Slot
                </button>
                <button
                  type="button"
                  onClick={clearTapSelection}
                  disabled={!selectedVehicleId && !selectedSlotId}
                  className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-40 hover:bg-gray-50"
                >
                  Cancel Selection
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || isArranging || !isDirty}
                  className="min-h-[44px] rounded-lg border border-green-300 bg-green-50 px-4 py-2 text-sm font-semibold text-green-800 disabled:opacity-50 hover:bg-green-100"
                >
                  Save Layout
                </button>
              </div>
            )}
          </div>
        )}

        {/* Warnings */}
        {(warnings.length > 0 || savedAt) && (
          <TrailerWarnings warnings={warnings} savedAt={savedAt} />
        )}

        {(Object.keys(canonicalTrailerPlan.rampStates ?? {}).length > 0 ||
          Object.keys(canonicalTrailerPlan.deckStates ?? {}).length > 0) && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
            <div className="font-semibold text-slate-900">Canonical trailer plan state</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ramps</div>
                {Object.entries(canonicalTrailerPlan.rampStates ?? {}).map(([id, state]) => (
                  <div key={id}>{id}: {String(state)}</div>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Decks</div>
                {Object.entries(canonicalTrailerPlan.deckStates ?? {}).map(([id, state]) => {
                  const value = state as any
                  return <div key={id}>{id}: height {value?.height ?? '—'}, angle {value?.angle ?? '—'}</div>
                })}
              </div>
            </div>
          </div>
        )}

        {/* Main 3-column layout */}
        <div className="grid grid-cols-[200px_1fr_320px] gap-4 items-start">
          {/* Left: Vehicle queue + resolve button */}
          <div className="sticky top-4 space-y-2">
            {/* Resolve Vehicle Fit Data button */}
            <button
              type="button"
              onClick={handleResolveProfiles}
              disabled={isResolvingProfiles}
              className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50 hover:bg-indigo-700"
            >
              {isResolvingProfiles ? 'Resolving…' : 'Resolve Vehicle Fit Data'}
            </button>
            {Object.keys(fitSummary).length > 0 && (
              <div className="text-[10px] text-gray-500 text-center">
                {Object.values(fitSummary).filter(s => s.status !== 'missing').length}/{Object.keys(fitSummary).length} profiles resolved
              </div>
            )}
            <VehicleQueue
              vehicles={unassignedVehicles}
              drag={drag}
              onDragStart={handleQueueDragStart}
              selectedVehicleId={selectedVehicleId}
              onSelectVehicle={selectVehicle}
              fitSummary={fitSummary}
            />
          </div>

          {/* Center: Trailer canvas */}
          <TrailerCanvas
            assignments={assignedVehicleMap}
            hoveredSlot={hoveredSlot}
            drag={drag}
            onVehicleDragStart={handleSlotDragStart}
            selectedVehicleId={selectedVehicleId}
            selectedSlotId={selectedSlotId}
            onSelectVehicle={selectVehicle}
            onSelectSlot={selectSlot}
          />

          {/* Right: Help panel */}
          <div className="bg-white rounded-xl border p-3 text-xs text-gray-500 space-y-3 sticky top-4">
            <div className="font-semibold text-gray-700 text-sm">Tips</div>
            <p>Drag vehicles from the queue on the left onto trailer slots.</p>
            <p>Drag placed vehicles between slots to rearrange.</p>
            <p><strong>Auto Arrange</strong> fills rear-lower slots first (last loaded, first off).</p>
            <p>Click <strong>Save Layout</strong> to persist to the driver's view.</p>
            <hr className="border-gray-100" />
            <p className="font-medium text-gray-600">Slot Layout</p>
            <p>Col 1 = Front (cab) → Col 4 = Rear (ramp)</p>
            <p>Row 1 = Upper deck · Row 2 = Lower deck</p>

            {/* ── Selected Equipment (always visible) ── */}
            {(selectedCombo || selectedTruckSpec || selectedTrailerSpec) && (
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-2.5 space-y-1.5 text-[10px]">
                <div className="text-[11px] font-semibold text-blue-700">Selected Equipment</div>
                {selectedCombo && (
                  <>
                    <div><span className="font-medium text-blue-600">Combo:</span> {selectedCombo.comboName}</div>
                    <div><span className="font-medium text-blue-600">Rig type:</span> {selectedCombo.rigType ?? '—'}</div>
                    <div><span className="font-medium text-blue-600">Capacity:</span> {selectedCombo.nominalVehicleCapacity ?? '—'} cars</div>
                    <div><span className="font-medium text-blue-600">Confidence:</span> {Math.round((selectedCombo.confidence ?? 0) * 100)}%</div>
                    <div><span className="font-medium text-blue-600">Status:</span> {selectedCombo.reviewStatus === 'manual_seed' ? 'Manual seed' : selectedCombo.reviewStatus}</div>
                    <div className="text-amber-700 font-medium">⚠ Verify against your actual build sheet.</div>
                  </>
                )}
                {!selectedCombo && selectedTruckSpec && (
                  <div><span className="font-medium text-blue-600">Tractor:</span> {selectedTruckSpec.manufacturer} {selectedTruckSpec.model}</div>
                )}
                {!selectedCombo && selectedTrailerSpec && (
                  <div><span className="font-medium text-blue-600">Trailer:</span> {selectedTrailerSpec.manufacturer} {selectedTrailerSpec.model}</div>
                )}
                {autoHaulerGeometry && (
                  <div><span className="font-medium text-blue-600">Slots:</span> {autoHaulerGeometry.slotMap?.length ?? '—'} ({autoHaulerGeometry.rigType})</div>
                )}
              </div>
            )}

            <details open className="pt-1">
              <summary className="cursor-pointer text-sm font-semibold text-gray-700">Equipment Profile</summary>
              <div className="mt-3 space-y-3 text-gray-700">

                {/* ── Combo selector ── */}
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2.5 space-y-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Equipment Combo</div>
                  <label className="space-y-1 block">
                    <span className="text-[11px] text-indigo-600">Quick-select tractor + trailer combo</span>
                    <select
                      value={selectedComboKey}
                      disabled={isApplyingCombo}
                      onChange={(e) => {
                        const key = e.target.value
                        setSelectedComboKey(key)
                        if (!key) { setSelectedCombo(null); setAutoHaulerGeometry(null) }
                        if (key) handleApplyCombo(key)
                      }}
                      className="w-full rounded border border-indigo-300 px-2 py-1 text-xs bg-white"
                    >
                      <option value="">Select combo preset</option>
                      {combos.map((c) => (
                        <option key={c.comboKey} value={c.comboKey}>
                          {c.comboName} ({c.nominalVehicleCapacity ?? '?'}-car)
                        </option>
                      ))}
                    </select>
                  </label>
                  {isApplyingCombo && <div className="text-[10px] text-indigo-600 animate-pulse">Applying combo…</div>}
                  {selectedCombo && !isApplyingCombo && (
                    <div className="rounded border border-indigo-100 bg-white px-2 py-1.5 text-[10px] text-gray-500 space-y-0.5">
                      <div><span className="font-medium text-indigo-700">Rig:</span> {selectedCombo.rigType ?? '—'}</div>
                      <div><span className="font-medium text-indigo-700">Capacity:</span> {selectedCombo.nominalVehicleCapacity ?? '?'} vehicles</div>
                      <div><span className="font-medium text-indigo-700">Conf:</span> {Math.round((selectedCombo.confidence ?? 0) * 100)}%</div>
                      <div className="text-amber-600 font-medium">Verify against your build sheet.</div>
                    </div>
                  )}
                  <p className="text-[10px] text-indigo-500">Or select tractor and trailer separately below.</p>
                </div>

                <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Tractor</div>
                  <label className="space-y-1 block">
                    <span className="text-[11px]">Truck Model Preset</span>
                    <select
                      value={selectedTruckSpecId}
                      onChange={(e) => {
                        const id = e.target.value
                        setSelectedTruckSpecId(id)
                        if (!id) setSelectedTruckSpec(null)
                        if (id) handleApplySpec(id, 'truck')
                      }}
                      className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                    >
                      <option value="">Select truck model</option>
                      {truckSpecs.map((spec) => (
                        <option key={spec.id} value={spec.id}>{spec.manufacturer} {spec.model}</option>
                      ))}
                    </select>
                  </label>
                  {selectedTruckSpec && (
                    <div className="rounded border border-gray-100 bg-gray-50 px-2 py-1.5 text-[10px] text-gray-500 space-y-0.5">
                      <div><span className="font-medium text-gray-600">Source:</span> {selectedTruckSpec.manufacturer}</div>
                      <div><span className="font-medium text-gray-600">Confidence:</span> {Math.round((selectedTruckSpec.confidence ?? 0) * 100)}%</div>
                      <div><span className="font-medium text-gray-600">Status:</span> {selectedTruckSpec.reviewStatus === 'auto_parsed' ? 'Auto parsed' : selectedTruckSpec.reviewStatus === 'needs_review' ? 'Needs review' : 'Manual seed'}</div>
                      {selectedTruckSpec.validationWarnings && selectedTruckSpec.validationWarnings.length > 0 && (
                        <div className="text-amber-600"><span className="font-medium">Warnings:</span> {selectedTruckSpec.validationWarnings.join(', ')}</div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <label className="space-y-1">
                      <span className="text-[11px]">Steer Axles</span>
                      <input value={equipmentProfile.tractorSteerAxles ?? ''} onChange={(e) => handleProfileIntegerChange('tractorSteerAxles', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Drive Axles</span>
                      <input value={equipmentProfile.tractorDriveAxles ?? ''} onChange={(e) => handleProfileIntegerChange('tractorDriveAxles', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Steer Limit (lbs)</span>
                      <input value={equipmentProfile.tractorSteerAxleLimit ?? ''} onChange={(e) => handleProfileIntegerChange('tractorSteerAxleLimit', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Drive Limit (lbs)</span>
                      <input value={equipmentProfile.tractorDriveAxleLimit ?? ''} onChange={(e) => handleProfileIntegerChange('tractorDriveAxleLimit', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Wheelbase</span>
                      <input value={equipmentProfile.tractorWheelbase ?? ''} onChange={(e) => handleProfileNumberChange('tractorWheelbase', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">GCWR (lbs)</span>
                      <input value={equipmentProfile.tractorGcwr ?? ''} onChange={(e) => handleProfileIntegerChange('tractorGcwr', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1 col-span-2">
                      <span className="text-[11px]">Tractor Empty Weight (lbs)</span>
                      <input value={equipmentProfile.tractorEmptyWeight ?? ''} onChange={(e) => handleProfileIntegerChange('tractorEmptyWeight', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Trailer</div>
                  <label className="space-y-1 block">
                    <span className="text-[11px]">Trailer Model Preset</span>
                    <select
                      value={selectedTrailerSpecId}
                      onChange={(e) => {
                        const id = e.target.value
                        setSelectedTrailerSpecId(id)
                        if (!id) setSelectedTrailerSpec(null)
                        if (id) handleApplySpec(id, 'trailer')
                      }}
                      className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                    >
                      <option value="">Select trailer model</option>
                      {trailerSpecs.map((spec) => (
                        <option key={spec.id} value={spec.id}>{spec.manufacturer} {spec.model}</option>
                      ))}
                    </select>
                  </label>
                  {selectedTrailerSpec && (
                    <div className="rounded border border-gray-100 bg-gray-50 px-2 py-1.5 text-[10px] text-gray-500 space-y-0.5">
                      <div><span className="font-medium text-gray-600">Source:</span> {selectedTrailerSpec.manufacturer}</div>
                      <div><span className="font-medium text-gray-600">Confidence:</span> {Math.round((selectedTrailerSpec.confidence ?? 0) * 100)}%</div>
                      <div><span className="font-medium text-gray-600">Status:</span> {selectedTrailerSpec.reviewStatus === 'auto_parsed' ? 'Auto parsed' : selectedTrailerSpec.reviewStatus === 'needs_review' ? 'Needs review' : 'Manual seed'}</div>
                      {selectedTrailerSpec.validationWarnings && selectedTrailerSpec.validationWarnings.length > 0 && (
                        <div className="text-amber-600"><span className="font-medium">Warnings:</span> {selectedTrailerSpec.validationWarnings.join(', ')}</div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <label className="space-y-1">
                      <span className="text-[11px]">Axles</span>
                      <input value={equipmentProfile.trailerAxles ?? ''} onChange={(e) => handleProfileIntegerChange('trailerAxles', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Axle Limit (lbs)</span>
                      <input value={equipmentProfile.trailerAxleLimit ?? ''} onChange={(e) => handleProfileIntegerChange('trailerAxleLimit', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Deck Length (in)</span>
                      <input value={equipmentProfile.trailerDeckLength ?? ''} onChange={(e) => handleProfileIntegerChange('trailerDeckLength', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Deck Width (in)</span>
                      <input value={equipmentProfile.trailerDeckWidth ?? ''} onChange={(e) => handleProfileIntegerChange('trailerDeckWidth', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Deck Height (in)</span>
                      <input value={equipmentProfile.trailerDeckHeight ?? ''} onChange={(e) => handleProfileIntegerChange('trailerDeckHeight', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px]">Ramp Length (in)</span>
                      <input value={equipmentProfile.trailerRampLength ?? ''} onChange={(e) => handleProfileIntegerChange('trailerRampLength', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                    <label className="space-y-1 col-span-2">
                      <span className="text-[11px]">Trailer Empty Weight (lbs)</span>
                      <input value={equipmentProfile.trailerEmptyWeight ?? ''} onChange={(e) => handleProfileIntegerChange('trailerEmptyWeight', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    </label>
                  </div>

                  <label className="flex items-center gap-2 text-[11px] text-gray-600">
                    <input type="checkbox" checked={equipmentProfile.isDoubleDeck} onChange={(e) => updateProfileField('isDoubleDeck', e.target.checked)} />
                    Double Deck Trailer
                  </label>
                </div>

                <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Overrides</div>
                  <label className="space-y-1 block">
                    <span className="text-[11px]">Override GCWR Limit (lbs)</span>
                    <input value={equipmentProfile.overrideGcwrLimit ?? ''} onChange={(e) => handleProfileIntegerChange('overrideGcwrLimit', e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-gray-600">
                    <input type="checkbox" checked={equipmentProfile.overrideAxleLimits} onChange={(e) => updateProfileField('overrideAxleLimits', e.target.checked)} />
                    Allow axle / GCWR override
                  </label>
                </div>

                <button
                  type="button"
                  onClick={handleProfileSave}
                  disabled={isSavingProfile || !isProfileDirty}
                  className="w-full rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {isSavingProfile ? 'Saving Profile…' : 'Save Equipment Profile'}
                </button>
              </div>
            </details>

            {/* ── Driver View: Equipment Summary ── */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 space-y-1.5 text-[10px]">
              <div className="text-[11px] font-semibold text-amber-800">Driver Equipment Summary</div>
              {selectedCombo ? (
                <>
                  <div><span className="font-medium text-amber-700">Combo:</span> {selectedCombo.comboName}</div>
                  <div><span className="font-medium text-amber-700">Rig type:</span> {selectedCombo.rigType ?? 'stinger'}</div>
                  <div><span className="font-medium text-amber-700">Capacity:</span> {selectedCombo.nominalVehicleCapacity ?? '—'} vehicles</div>
                </>
              ) : (
                <>
                  {selectedTruckSpec && <div><span className="font-medium text-amber-700">Tractor:</span> {selectedTruckSpec.manufacturer} {selectedTruckSpec.model}</div>}
                  {selectedTrailerSpec && <div><span className="font-medium text-amber-700">Trailer:</span> {selectedTrailerSpec.manufacturer} {selectedTrailerSpec.model}</div>}
                  {!selectedTruckSpec && !selectedTrailerSpec && <div className="text-gray-400 italic">No equipment selected</div>}
                </>
              )}
              <div className="text-amber-700 font-medium pt-0.5">⚠ Planning aid only. Verify against build sheet, equipment plate, and regulations.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Ghost card — follows pointer during drag */}
      {drag && dragVehicle && (
        <div
          className="fixed z-50 pointer-events-none rounded-lg bg-white border-2 border-blue-400 shadow-xl px-3 py-2 text-sm font-medium text-gray-800 max-w-[150px] opacity-90"
          style={{ left: drag.x - 75, top: drag.y - 30 }}
        >
          <div className="font-semibold text-gray-900 leading-tight text-xs">{vehicleLabel(dragVehicle)}</div>
          <div className="text-[9px] text-gray-400 font-mono">…{dragVehicle.vin.slice(-6)}</div>
          <div
            className="w-full h-1 rounded-full mt-1"
            style={{ background: vehicleColorHex(dragVehicle.color) }}
          />
        </div>
      )}
    </div>
  )
}
